import { dateToInputString, inputStringToDate } from "@/common/helpers/date-input";
import { cn } from "@/common/helpers/utils";
import { DatePickerInput } from "@/components/ui/date-picker-input";
import { DateFormat, userConfigStore } from "@/store/user-config-store";
import { MoveRightIcon } from "lucide-react";

export interface DateRangePickerInputProps {
  /** Locale-formatted date string for the start of the range, or empty string if unset. */
  from: string;
  /** Locale-formatted date string for the end of the range, or empty string if unset. */
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  disabled?: boolean;
  futureDaysDisabled?: boolean;
  pastDaysDisabled?: boolean;
  className?: string;
}

/**
 * A date range picker composed of two `DatePickerInput` fields.
 * Each field supports direct text input (locale-aware) and a calendar popover.
 * The "to" calendar automatically disables dates before the selected "from" date.
 * Clearing "from" also clears "to" to prevent an invalid range.
 */
export function DateRangePickerInput({
  from,
  to,
  onFromChange,
  onToChange,
  disabled,
  futureDaysDisabled,
  pastDaysDisabled,
  className,
}: DateRangePickerInputProps) {
  const dateFormat = userConfigStore((state) => state.dateFormat);
  const fromDate = inputStringToDate(from, { dateFormat });

  function handleFromChange(value: string) {
    onFromChange(value);

    if (to) {
      const newFromDate = inputStringToDate(value, { dateFormat });
      const toDate = inputStringToDate(to, { dateFormat });
      if (newFromDate && toDate && toDate < newFromDate) {
        onToChange("");
      }
    }
  }

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <DatePickerInput
        value={from}
        onChange={handleFromChange}
        disabled={disabled}
        futureDaysDisabled={futureDaysDisabled}
        pastDaysDisabled={pastDaysDisabled}
      />
      <MoveRightIcon className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
      <DatePickerInput
        value={to}
        onChange={onToChange}
        disabled={disabled}
        futureDaysDisabled={futureDaysDisabled}
        pastDaysDisabled={pastDaysDisabled}
        disabledDates={fromDate ? { before: fromDate } : undefined}
      />
    </div>
  );
}

/**
 * Converts a locale-formatted date string to an ISO date string (`YYYY-MM-DD`).
 * Returns `undefined` if the input is empty or invalid.
 */
export function dateRangeInputToIso(value: string, dateFormat: DateFormat): string | undefined {
  const date = inputStringToDate(value, { dateFormat });
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Converts an ISO date string (`YYYY-MM-DD`) to a locale-formatted string
 * suitable for a `DateRangePickerInput` field.
 */
export function isoToDateRangeInput(isoDate: string | undefined, dateFormat: DateFormat): string {
  if (!isoDate) return "";
  const date = inputStringToDate(isoDate, { dateFormat: DateFormat.ISO });
  if (!date) return "";
  return dateToInputString(date, { dateFormat });
}
